import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { AtwLogo } from "@/components/AtwLogo";
import { api, type LoginResponse } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  authHubSignInUrlForHub,
  fetchHubSsoTicket,
  preferAuthHubSso,
} from "@/lib/atwAuthHub";
import { hasPendingSlackClaim } from "@/lib/slackCustomer";
import { HUB_VERSION } from "@/lib/version";

export function LoginPage() {
  const { user, ready, signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const ssoReturn = searchParams.get("sso") === "1";
  const useAuthHub = preferAuthHubSso();
  const [slackPending] = useState(hasPendingSlackClaim);
  const slackNote = slackPending ? (
    <p className="auth-note">Sign in to ATW Hub to finish connecting Slack.</p>
  ) : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ssoBusy, setSsoBusy] = useState(useAuthHub);

  useEffect(() => {
    if (!useAuthHub || !ready || user) return;

    let cancelled = false;

    async function exchangeAuthHubSession() {
      setSsoBusy(true);
      setError("");
      try {
        const { ticket } = await fetchHubSsoTicket();
        if (cancelled) return;
        const result = await api<LoginResponse>("/api/auth/atw-account", {
          method: "POST",
          body: JSON.stringify({ ticket }),
        });
        if (cancelled) return;
        signIn(result);
      } catch (err) {
        if (cancelled) return;
        // No Auth-hub session yet (or ticket failed) — send them to auth.atw.net.
        if (!ssoReturn) {
          window.location.replace(authHubSignInUrlForHub("/login?sso=1"));
          return;
        }
        setError(err instanceof Error ? err.message : "Could not continue with ATW Auth.");
        setSsoBusy(false);
      }
    }

    void exchangeAuthHubSession();
    return () => {
      cancelled = true;
    };
  }, [useAuthHub, ready, user, ssoReturn, signIn]);

  if (ready && user) return <Navigate to="/mail" replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<LoginResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
      });
      signIn(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  }

  if (useAuthHub) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-logo">
            <AtwLogo size={56} label="ATW Hub" />
          </div>
          <h1>ATW Hub</h1>
          <p className="auth-sub">Continuing with your ATW account…</p>
          {slackNote}
          {error ? (
            <>
              <p className="auth-error">{error}</p>
              <button
                type="button"
                disabled={ssoBusy}
                onClick={() => {
                  window.location.href = authHubSignInUrlForHub("/login?sso=1");
                }}
              >
                Sign in with ATW
              </button>
            </>
          ) : (
            <p className="auth-sub">{ssoBusy || !ready ? "Signing in…" : "Redirecting…"}</p>
          )}
          <p className="auth-version">{HUB_VERSION}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={onSubmit} autoComplete="on">
        <div className="auth-logo">
          <AtwLogo size={56} label="ATW Hub" />
        </div>
        <h1>ATW Hub</h1>
        <p className="auth-sub">Mail, chat, calendar, and meetings</p>
        {slackNote}
        <label>
          Email
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error ? <p className="auth-error">{error}</p> : null}
        <button type="submit" disabled={busy || !ready}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="auth-version">{HUB_VERSION}</p>
      </form>
    </div>
  );
}
